import React, { useState, useEffect, useRef, ChangeEvent, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatHeader from '../components/chat/ChatHeader';
import MessageThread from '../components/chat/MessageThread';
import ChatInput from '../components/chat/ChatInput';
import ApiSettings from '../components/common/ApiSettings';
import { useChat } from '../context/ChatContext';
import { cx } from '../styles';
import { chat } from '../styles/components';
import { getSession, importSession } from '../utils/sessionStorage';
import { getConversationStatus, getSessionFromServer } from '../utils/api';
import { TaskStatus } from '../utils/types';
import { updateSessionMemoryContext } from '../utils/sessionStorage';
import { generateMessageId } from '../utils/messageUtils';
interface ChatPageProps {
  onToggleSidebar?: () => void;
  isMobile?: boolean;
}

const ChatPage: React.FC<ChatPageProps> = ({ 
  onToggleSidebar,
  isMobile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get('sessionId');
  const scrollContainerRef = useRef<HTMLDivElement>(null); // Create ref for the scroll container
  const { 
    loading, 
    addUserMessage, 
    handleRelatedQueryClick,
    currentSessionId,
    setCurrentSessionId,
    memoryContext,
    setMemoryContext,
    processUserMessageAsync,
    apiSettings,
    setApiSettings,
    processingState,
    setProcessingState
  } = useChat();
  
  // Derive messages from memoryContext
  const messages = useMemo(() => {
    return memoryContext?.conversation_history || [];
  }, [memoryContext?.conversation_history]);
  const [input, setInput] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showSourcesTab, setShowSourcesTab] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [selectedSourceIndex, setSelectedSourceIndex] = useState<number | null>(null);
  
  const [loadingSharedSession, setLoadingSharedSession] = useState<boolean>(false);
  
  // Check session validity and redirect if needed
  useEffect(() => {
    // If no sessionId, redirect to home
    if (!sessionId) {
      console.log('[ChatPage] No session ID in URL, redirecting to home');
      navigate('/', { replace: true });
      return;
    }
    
    // First check if session exists locally
    const localSession = getSession(sessionId);
    
    const loadSession = async () => {
      if (localSession) {
        // Local session found, use it
        console.log(`[ChatPage] Loading local session: ${sessionId}`);
        setCurrentSessionId(sessionId);
      } else {
        // Session not found locally, try to fetch from server
        console.log(`[ChatPage] Session ${sessionId} not found locally, attempting to fetch from server`);
        try {
          // Show loading indicator
          setLoadingSharedSession(true);
          
          // Fetch session from server
          const serverSession = await getSessionFromServer(sessionId);
          
          // Import the session to local storage
          const importedSession = importSession(sessionId, serverSession);

          if (importedSession) {  
            // Set current session
            console.log(`[ChatPage] Successfully imported shared session: ${sessionId}`);
            setCurrentSessionId(sessionId);
          } else {
            console.error(`[ChatPage] Failed to import shared session: ${sessionId}`);
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error(`[ChatPage] Error fetching shared session: ${sessionId}`, error);
          navigate('/', { replace: true });
        } finally {
          setLoadingSharedSession(false);
        }
      }
      
      // Check if the conversation already has processing happening
      const checkConversationStatus = async () => {
        try {
          const status = await getConversationStatus(sessionId);
          
          // If the conversation is currently being processed, update the processing state
          if (status && status.status === TaskStatus.PROCESSING) {
            console.log(`[ChatPage] Conversation ${sessionId} is already processing`);
            setProcessingState({
              current_cycle: status.current_cycle || 0,
              max_cycles: status.max_cycles || apiSettings.maxCycles,
              progress: status.progress ? status.progress / 100 : 0
            });
          }
        } catch (error) {
          console.error("Error checking conversation status:", error);
        }
      };
      
      checkConversationStatus();
    };
    
    loadSession();
  }, [sessionId, currentSessionId, setCurrentSessionId, navigate, apiSettings.maxCycles, setProcessingState]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);
  
  // Handle polling for conversation status
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    // Only poll if the last message is from the user and we're in loading state
    // (loading state indicates processing has started)
    console.log(`[ChatPage] Polling for conversation status: ${loading}, ${messages.length}`);
    if (messages.length > 0 && loading) {
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage.role === 'user') {
        console.log(`[ChatPage] Starting polling for status updates`);
        // Start polling for status updates
        pollInterval = setInterval(async () => {
          console.log(`[ChatPage] Polling for status updates, currentSessionId: ${currentSessionId}`);
          try {
            if (!currentSessionId) return;
            
            const status = await getConversationStatus(currentSessionId);
            
            // Update processing state for UI
            if (status) {
              setProcessingState({
                current_cycle: status.current_cycle || 0,
                max_cycles: status.max_cycles || apiSettings.maxCycles,
                progress: status.progress ? status.progress / 100 : 0
              });
              
              // If completed, update memory context and stop polling
              if (status.status === TaskStatus.COMPLETED && status.result?.memory_context) {
                setMemoryContext(status.result.memory_context);
                console.log(`[ChatPage] Updating local storage for session: ${currentSessionId}`);
                updateSessionMemoryContext(currentSessionId, status.result.memory_context);
                setProcessingState(null);
                clearInterval(pollInterval!);
              } else if (status.status === TaskStatus.ERROR) {
                // Handle error - show error message to user
                console.error(`Error processing request: ${status.error}`);
                
                // Add error message to the chat
                const timestamp = new Date().toISOString();
                const errorMessage = status.error || "Sorry, there was an error processing your request. Please try again.";
                
                // Update memory context with error message
                const errorMsg = {
                  id: generateMessageId(memoryContext?.conversation_history || [], errorMessage, 'assistant', timestamp),
                  role: 'assistant' as const,
                  content: errorMessage,
                  timestamp
                };
                
                // Create new memory context
                if (!memoryContext) {
                  setMemoryContext({
                    conversation_history: [errorMsg],
                    execution_history: []
                  });
                  // Update local storage
                  console.log(`[ChatPage] Updating local storage for session: ${currentSessionId}`);
                  updateSessionMemoryContext(currentSessionId, memoryContext);
                } else {
                  const updatedMemoryContext = {
                    ...memoryContext,
                    conversation_history: [
                      ...memoryContext.conversation_history,
                      errorMsg
                    ]
                  };
                  setMemoryContext(updatedMemoryContext);
                  // Update local storage
                  console.log(`[ChatPage] Updating local storage for session: ${currentSessionId}`);
                  updateSessionMemoryContext(currentSessionId, memoryContext);
                }
                setProcessingState(null);
                clearInterval(pollInterval!);
              }
            }
          } catch (error) {
            console.error("Polling error:", error);
            // Don't stop polling on network errors unless it's been too many retries
          }
        }, 2000);
      }
    }
    
    // Clean up interval on unmount or when dependencies change
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [messages, loading, currentSessionId, apiSettings.maxCycles, setMemoryContext, setProcessingState, memoryContext]);

  const handleSendMessage = () => {
    if (input.trim()) {
      const message = input.trim();
      setInput('');
      const sessionId = new URLSearchParams(location.search).get('sessionId');
      
      // Create a new user message
      const timestamp = new Date().toISOString();
      const userMessage = {
        id: generateMessageId(memoryContext?.conversation_history || [], message, 'user', timestamp),
        content: message,
        role: 'user' as const,
        timestamp
      };
      
      // Start processing the message
      if (sessionId && memoryContext) {
        // Important: Create a local updated memory context that includes the new message
        const updatedMemoryContext = {
          ...memoryContext,
          conversation_history: [
            ...memoryContext.conversation_history,
            userMessage
          ]
        };
        
        // First add the message to the UI
        addUserMessage(message);
        
        // Then process with the updated context that includes the new message
        processUserMessageAsync(updatedMemoryContext, sessionId, apiSettings);
        
        // Initialize processing state (since the pure function doesn't do state updates)
        setProcessingState({
          current_cycle: 0,
          max_cycles: apiSettings.maxCycles || 2,
          progress: 0
        });
      } else {
        console.error('[ChatPage] No session ID or memory context found');
      }
    }
  };

  const handleSourceClick = (index: number) => {
    setSelectedSourceIndex(index);
    setShowSourcesTab(true);
  };

  const toggleSourcesTab = () => {
    setShowSourcesTab(prevState => !prevState);
  };

  // Get all citations from all bot messages
  const allCitations = useMemo(() => {
    return messages
      .filter((msg: { role: string, citations?: string[] }) => 
        msg.role === 'assistant' && msg.citations && msg.citations.length > 0)
      .flatMap((msg: { citations?: string[] }) => msg.citations || []);
  }, [messages]);
    
  // Check if we have any citations to show
  const hasCitations = useMemo(() => allCitations.length > 0, [allCitations]);

  // Close sources tab if no citations available
  useEffect(() => {
    if (!hasCitations && showSourcesTab) {
      setShowSourcesTab(false);
    }
  }, [hasCitations, showSourcesTab]);

  return (
    <div className={cx(
      'flex flex-col h-screen', // Changed h-full to h-screen to ensure full viewport height
      'bg-page'
    )}>
      <ChatHeader
        showSourcesTab={showSourcesTab}
        showSettingsPanel={showSettingsPanel}
        onToggleSourcesTab={hasCitations ? toggleSourcesTab : undefined}
        onToggleSettingsPanel={() => setShowSettingsPanel(!showSettingsPanel)}
        onToggleSidebar={onToggleSidebar}
        isMobile={isMobile}
      />
      
      {/* Middle section: Chat + Sources */}
      <div className={cx("flex-1 flex", "bg-page", "relative")}> 
        {/* Main chat area - Assign ref here */}
        <div 
          ref={scrollContainerRef} 
          className={cx(
            "flex-1 overflow-y-auto p-4", // This is the scroll container
            "absolute inset-0",
            (showSourcesTab || showSettingsPanel) ? "right-64 md:right-80" : "right-0"
          )}
        >
          <div className="max-w-3xl mx-auto">
            {loadingSharedSession ? (
              <div className="flex flex-col items-center justify-center h-full py-16">
                <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-secondary">Loading shared conversation...</p>
              </div>
            ) : (
              <MessageThread 
                messages={messages} 
                loading={loading}
                onRelatedQueryClick={(query) => {
                  const sessionId = new URLSearchParams(location.search).get('sessionId');
                  
                  // Create a new user message for the related query
                  const timestamp = new Date().toISOString();
                  const userMessage = {
                    id: generateMessageId(memoryContext?.conversation_history || [], query, 'user', timestamp),
                    content: query,
                    role: 'user' as const,
                    timestamp
                  };
                  
                  // Start processing the message
                  if (sessionId && memoryContext) {
                    // Important: Create a local updated memory context that includes the new message
                    const updatedMemoryContext = {
                      ...memoryContext,
                      conversation_history: [
                        ...memoryContext.conversation_history,
                        userMessage
                      ]
                    };
                    
                    // First add the message to the UI
                    handleRelatedQueryClick(query);
                    
                    // Then process with the updated context that includes the new message
                    processUserMessageAsync(updatedMemoryContext, sessionId, apiSettings);
                    
                    // Initialize processing state (since the pure function doesn't do state updates)
                    setProcessingState({
                      current_cycle: 0,
                      max_cycles: apiSettings.maxCycles || 2,
                      progress: 0
                    });
                  } else {
                    console.error('[ChatPage] No session ID or memory context found');
                  }
                }}
                onSourceClick={handleSourceClick}
              />
            )}
            
            {/* Progress bar */}
            {loading && (
              <div className={chat.progressContainer}>
                <div className="flex justify-between w-64 mb-1">
                  <span className={chat.progressText}>
                    {processingState?.current_cycle 
                      ? `Cycle ${processingState.current_cycle}/${processingState.max_cycles}`
                      : `Initializing...`}
                  </span>
                  <span className={chat.progressText}>
                    {processingState?.progress 
                      ? `${Math.round(processingState.progress * 100)}%` 
                      : ''}
                  </span>
                </div>
                <div className={chat.progressTrack}>
                  <div 
                    className={chat.progressFill} 
                    style={{
                      width: `${processingState?.progress ? Math.round(processingState.progress * 100) : 5}%`
                    }}
                  ></div>
                </div>
                <span className={chat.progressText}>Processing your request...</span>
              </div>
            )}
            
            {/* Invisible element at the end of messages for scrolling */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Settings panel */}
        {showSettingsPanel && (
          <div className={cx(
            "w-64 md:w-80 overflow-y-auto",
            "border-l border-divider",
            "bg-component",
            "absolute right-0 top-0 bottom-0"
          )}>
            <div className="p-3 border-b border-divider flex justify-between items-center">
              <h3 className="font-medium text-primary">API Settings</h3>
              <button 
                onClick={() => setShowSettingsPanel(false)}
                className="text-tertiary hover:text-primary p-1 rounded-full"
                aria-label="Close settings panel"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <ApiSettings
                onSettingsChange={(settings) => {
                  setApiSettings(settings);
                }}
              />
            </div>
          </div>
        )}

        {/* Sources panel */}
        {showSourcesTab && (
          <div className={cx(
            "w-64 md:w-80 overflow-y-auto",
            "border-l border-divider",
            "bg-component",
            "absolute right-0 top-0 bottom-0"
          )}>
            <div className="p-3 border-b border-divider flex justify-between items-center">
              <h3 className="font-medium text-primary">Sources</h3>
              <button 
                onClick={() => setShowSourcesTab(false)}
                className="text-tertiary hover:text-primary p-1 rounded-full"
                aria-label="Close sources panel"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="divide-y divide-divider">
              {allCitations.map((citation: string, index: number) => (
                <div
                  key={index}
                  className={cx(
                    "p-3 flex items-start",
                    selectedSourceIndex === index ? "bg-hover" : ""
                  )}
                >
                  <span className="mr-2 text-sm text-tertiary">[{index + 1}]</span>
                  <div dangerouslySetInnerHTML={{ __html: citation }} />
                </div>
              ))}

              {allCitations.length === 0 && (
                <div className="p-4 text-sm text-tertiary text-center">
                  No sources available for this conversation.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className={cx(
        "border-t p-4", 
        "border-divider", 
        "bg-page"
      )}>
        <div className={cx(
          "mx-auto",
          (showSourcesTab || showSettingsPanel) ? "max-w-2xl" : "max-w-3xl"
        )}>
          <ChatInput 
            value={input}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
            onSend={handleSendMessage}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
